import { useState, useMemo } from 'react'
import type { RawColumn } from '../pages/Warehouse/types'

// ── Types ─────────────────────────────────────────────────────────────────────

export type MappingTarget =
  | 'skip' | 'hbl_no' | 'job_ref' | 'client_ref' | 'system_ref'
  | 'split_no' | 'container_no' | 'description' | 'expected_cartons'
  | 'expected_weight_kg' | 'net_weight_kg' | 'expected_cbm'
  | 'delivery_method' | 'delivery_address' | 'delivery_postcode'
  | 'delivery_contact' | 'delivery_tracking_no' | 'vat_number'
  | 'amazon_ref_id' | 'shipment_id' | 'appointment_code'
  | 'physical_warehouse' | 'notes'

export interface MappingOption {
  value: MappingTarget
  label: string
  required?: boolean
}

export const MAPPING_OPTIONS: MappingOption[] = [
  { value: 'skip',                 label: 'Skip (ignore this column)' },
  { value: 'hbl_no',              label: 'HBL / Bill of Lading No.' },
  { value: 'job_ref',             label: 'Job Ref (KI/KE number)' },
  { value: 'client_ref',          label: 'Customer Reference (customer\'s internal reference)' },
  { value: 'system_ref',          label: 'N/M / Marks' },
  { value: 'split_no',            label: 'Split No' },
  { value: 'container_no',        label: 'Container No' },
  { value: 'description',         label: 'Description *', required: true },
  { value: 'expected_cartons',    label: 'Cartons *', required: true },
  { value: 'expected_weight_kg',  label: 'Weight Gross (KG)' },
  { value: 'net_weight_kg',       label: 'Weight Net (KG)' },
  { value: 'expected_cbm',        label: 'CBM' },
  { value: 'delivery_method',     label: 'Delivery Method *', required: true },
  { value: 'delivery_address',    label: 'Delivery Address *', required: true },
  { value: 'delivery_postcode',   label: 'Postcode' },
  { value: 'delivery_contact',    label: 'Contact / Phone' },
  { value: 'delivery_tracking_no', label: 'Tracking No' },
  { value: 'vat_number',          label: 'VAT Number' },
  { value: 'amazon_ref_id',       label: 'Amazon Ref ID' },
  { value: 'shipment_id',         label: 'Shipment ID' },
  { value: 'appointment_code',    label: 'Appt / EORI' },
  { value: 'physical_warehouse',  label: 'Physical Warehouse' },
  { value: 'notes',               label: 'Notes / Remarks' },
]

// Edge function skips a row only when BOTH description AND cartons are null.
// delivery_method / delivery_address are important but not parse-blocking.
const REQUIRED_FIELDS: MappingTarget[] = ['description', 'expected_cartons']
const RECOMMENDED_FIELDS: MappingTarget[] = ['delivery_method', 'delivery_address']

// ── Auto-suggestion ───────────────────────────────────────────────────────────

interface SuggestionRule {
  target: MappingTarget
  keywords: string[]
  sampleTest?: (samples: string[]) => boolean
}

const RULES: SuggestionRule[] = [
  { target: 'hbl_no',              keywords: ['hbl', 'hbl no', 'hbl.', 'bill of lading', 'bol', 'b/l', '提单号', '提单'] },
  { target: 'job_ref',             keywords: ['job ref', 'job reference', 'job no', 'jobref'] },
  { target: 'split_no',            keywords: ['split no', 'split', '分单号', '分拆号'] },
  { target: 'container_no',        keywords: ['container', 'cntr', 'container no', '集装箱'] },
  { target: 'description',         keywords: ['description', 'desc', '货描', '品名', '货物描述', '描述', 'goods', 'commodity'] },
  { target: 'system_ref',          keywords: ['外箱找货唛头', '唛头', '麦头', '外箱唛头', 'marks', 'mark'],
    sampleTest: s => s.some(v => v.length <= 12 && /^[\w#\-\/]+$/.test(v)) },
  { target: 'client_ref',          keywords: ['client ref', 'customer ref', 'cust ref', '客户参考', '内部参考'],
    sampleTest: s => s.some(v => v.length >= 8) },
  { target: 'expected_cartons',    keywords: ['ctns', 'cartons', '件数', '箱数', 'pkgs', 'pieces', 'qty', 'pcs'],
    sampleTest: s => s.some(v => /^\d+$/.test(v)) },
  { target: 'expected_weight_kg',  keywords: ['gross weight', 'gross wt', 'g.w', 'gw', '毛重', 'weight kg', 'wt(kg)', 'kgs', 'wt'],
    sampleTest: s => s.some(v => /^\d+(\.\d+)?$/.test(v)) },
  { target: 'net_weight_kg',       keywords: ['net weight', 'net wt', 'n.w', 'nw', '净重'],
    sampleTest: s => s.some(v => /^\d+(\.\d+)?$/.test(v)) },
  { target: 'expected_cbm',        keywords: ['cbm', '方数', 'volume', 'vol', 'm3', 'cubic'],
    sampleTest: s => s.some(v => /^\d+\.\d+$/.test(v)) },
  { target: 'delivery_method',     keywords: ['method', '派送方式', '送货方式', '运输方式', 'delivery method', 'del method', '配送方式', '送货方法'],
    sampleTest: s => s.some(v => /dpd|卡派|amazon|fba|courier|快递|自提|truck|haulage|evri|royal mail/i.test(v)) },
  { target: 'delivery_address',    keywords: ['address', 'add', 'addr', '地址', '收货地址', '目的地', 'delivery address', 'ship to', 'destination'] },
  { target: 'delivery_postcode',   keywords: ['postcode', 'post code', 'pc', '邮编', 'zip', 'postal code'] },
  { target: 'delivery_contact',    keywords: ['contact', 'tel', 'phone', '联系人', '电话', 'delivery contact', 'recipient'] },
  { target: 'delivery_tracking_no', keywords: ['tracking', 'delivery mode', 'track no', 'tracking no', '快递单号', 'waybill'],
    sampleTest: s => s.some(v => /^\d{10,}$/.test(v) || /^[0-9A-Z]{10,}$/i.test(v)) },
  { target: 'vat_number',          keywords: ['vat', 'vat no', 'vat number', '税号', '增值税'] },
  { target: 'amazon_ref_id',       keywords: ['amazon', 'amazon ref', 'asin', 'fnsku', 'amazon ref id', 'ote', 'ota'] },
  { target: 'shipment_id',         keywords: ['shipment id', 'shipment', 'ship id', '发货单'] },
  { target: 'appointment_code',    keywords: ['appointment', 'appt', 'appt code', 'eori', '预约码', '预约'] },
  { target: 'physical_warehouse',  keywords: ['physical warehouse', '物理仓', 'warehouse', 'wh'] },
  { target: 'notes',               keywords: ['notes', 'note', 'remarks', 'remark', '备注', 'comment', '注意', 'info'] },
]

// 'n/m' header is ambiguous (could be client_ref or system_ref), handled specially below

export function suggestMapping(columns: RawColumn[]): Record<number, { target: MappingTarget; confidence: number }> {
  const result: Record<number, { target: MappingTarget; confidence: number }> = {}

  for (const col of columns) {
    const header = col.header.trim().toLowerCase()
    let bestTarget: MappingTarget = 'skip'
    let bestConf = 0

    // Special case: 'n/m' — check samples to disambiguate marks vs client ref
    if (header === 'n/m') {
      const hasLongSamples = col.samples.some(v => v.length > 10)
      if (hasLongSamples) {
        // Looks like a long reference, not marks
        result[col.index] = { target: 'client_ref', confidence: 60 }
        continue
      }
      result[col.index] = { target: 'system_ref', confidence: 75 }
      continue
    }

    for (const rule of RULES) {
      let conf = 0
      if (rule.keywords.some(k => k === header)) conf = 90
      else if (rule.keywords.some(k => header.includes(k) || k.includes(header))) conf = 70

      if (conf > 0 && rule.sampleTest) {
        if (rule.sampleTest(col.samples)) conf = Math.min(95, conf + 8)
        else conf = Math.max(0, conf - 15)  // penalty if samples don't match
      }

      if (conf > bestConf) { bestConf = conf; bestTarget = rule.target }
    }

    if (bestConf > 0) result[col.index] = { target: bestTarget, confidence: bestConf }
  }

  // Dedup: if same target suggested twice, keep higher confidence only
  const seen = new Map<MappingTarget, { idx: number; conf: number }>()
  for (const [idxStr, s] of Object.entries(result)) {
    const idx = parseInt(idxStr)
    const prev = seen.get(s.target)
    if (!prev) { seen.set(s.target, { idx, conf: s.confidence }) }
    else if (s.confidence > prev.conf) { result[prev.idx] = { target: 'skip', confidence: 0 }; seen.set(s.target, { idx, conf: s.confidence }) }
    else { result[idx] = { target: 'skip', confidence: 0 } }
  }

  // Remove 'skip' entries (they're default)
  for (const [idxStr, s] of Object.entries(result)) {
    if (s.target === 'skip') delete (result as any)[idxStr]
  }

  return result
}

// ── Item preview from current mapping ────────────────────────────────────────

function previewItems(_columns: RawColumn[], rawRows: any[][], headerRowIdx: number, mapping: Record<number, MappingTarget>) {
  const fieldToCol = new Map<string, number>()
  for (const [idxStr, target] of Object.entries(mapping)) {
    if (target !== 'skip') fieldToCol.set(target, parseInt(idxStr))
  }
  const get = (row: any[], field: string) => {
    const idx = fieldToCol.get(field)
    return idx !== undefined ? String(row[idx] ?? '').trim() : ''
  }

  const items: Array<Record<string, string>> = []
  for (let ri = headerRowIdx + 1; ri < rawRows.length && items.length < 5; ri++) {
    const row = rawRows[ri]
    if (!row || row.every((c: any) => c === null || c === undefined || String(c).trim() === '')) continue
    const ctns = get(row, 'expected_cartons')
    const desc = get(row, 'description')
    if (!desc && !ctns) continue
    const item: Record<string, string> = {}
    for (const [field] of fieldToCol) item[field] = get(row, field)
    items.push(item)
  }
  return items
}

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  columns: RawColumn[]
  rawRows: any[][]
  headerRowIdx: number
  initialMapping?: Record<number, MappingTarget>
  onConfirm: (mapping: Record<number, MappingTarget>) => void
  onClose: () => void
  confirming?: boolean
}

const PREVIEW_FIELDS: Array<{ key: MappingTarget; label: string }> = [
  { key: 'hbl_no',           label: 'HBL' },
  { key: 'job_ref',          label: 'Job Ref' },
  { key: 'split_no',         label: 'Split' },
  { key: 'description',      label: 'Description' },
  { key: 'expected_cartons', label: 'Ctns' },
  { key: 'delivery_method',  label: 'Method' },
  { key: 'delivery_address', label: 'Address' },
  { key: 'client_ref',       label: 'Client Ref' },
  { key: 'system_ref',       label: 'N/M' },
  { key: 'delivery_tracking_no', label: 'Tracking' },
]

export default function ColumnMappingTable({ columns, rawRows, headerRowIdx, initialMapping, onConfirm, onClose, confirming = false }: Props) {
  const suggestions = useMemo(() => suggestMapping(columns), [columns])

  const [mapping, setMapping] = useState<Record<number, MappingTarget>>(() => {
    if (initialMapping) return initialMapping
    const m: Record<number, MappingTarget> = {}
    for (const [idxStr, s] of Object.entries(suggestions)) m[parseInt(idxStr)] = s.target
    return m
  })

  // Track which rows the user has manually touched — clears ⚠ indicator even if
  // the chosen value happens to match the (low-confidence) AI suggestion.
  const [userTouched, setUserTouched] = useState<Record<number, boolean>>({})

  const set = (colIdx: number, target: MappingTarget) => {
    setMapping(prev => ({ ...prev, [colIdx]: target }))
    setUserTouched(prev => ({ ...prev, [colIdx]: true }))
  }

  const preview = useMemo(() => previewItems(columns, rawRows, headerRowIdx, mapping), [mapping, columns, rawRows, headerRowIdx])

  const missingRequired    = REQUIRED_FIELDS.filter(f => !Object.values(mapping).includes(f))
  const missingRecommended = RECOMMENDED_FIELDS.filter(f => !Object.values(mapping).includes(f))

  const mappedFields = new Set<MappingTarget>(Object.values(mapping).filter(v => v !== 'skip'))

  // Label for what a column index is mapped to
  const optionLabel = (t: MappingTarget) => MAPPING_OPTIONS.find(o => o.value === t)?.label ?? t

  const previewCols = PREVIEW_FIELDS.filter(f => mappedFields.has(f.key))

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-7xl max-h-[95vh] flex flex-col">

        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Column Mapping</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {columns.length} columns detected
              {missingRequired.length > 0 ? (
                <span className="text-red-600 font-medium ml-1">
                  · missing required: {missingRequired.map(f => optionLabel(f).replace(' *', '')).join(', ')}
                </span>
              ) : missingRecommended.length > 0 ? (
                <span className="text-amber-600 ml-1">
                  · ✓ required mapped · recommended not mapped: {missingRecommended.map(f => optionLabel(f).replace(' *', '')).join(', ')}
                </span>
              ) : (
                <span className="text-green-600 font-medium ml-1">· ✓ all fields mapped</span>
              )}
              {' '}· ⚠ = low confidence
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">

          {/* Mapping table */}
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide w-12">Col</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide">Header</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide">Samples</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide w-64">Map To</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {columns.map(col => {
                  const suggestion = suggestions[col.index]
                  const current = mapping[col.index] ?? 'skip'
                  const touched = userTouched[col.index] === true
                  // ⚠ only when: AI suggested it with low confidence AND user hasn't touched this row
                  const lowConf = !touched && suggestion && suggestion.confidence < 70 && current === suggestion.target
                  const isRequired = REQUIRED_FIELDS.includes(current as MappingTarget)

                  return (
                    <tr key={col.index} className={`${isRequired ? 'bg-blue-50/30 dark:bg-blue-900/10' : ''} hover:bg-gray-50 dark:hover:bg-gray-700/30`}>
                      <td className="px-3 py-2 font-mono text-gray-500 font-medium">{col.letter}</td>
                      <td className="px-3 py-2">
                        <span className="font-medium text-gray-800 dark:text-gray-200">{col.header}</span>
                      </td>
                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400 max-w-xs">
                        <span className="font-mono text-[11px]">{col.samples.slice(0, 3).join(', ')}</span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          <select
                            value={current}
                            onChange={e => set(col.index, e.target.value as MappingTarget)}
                            className={`text-xs border rounded-lg px-2 py-1 bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 w-full ${
                              current === 'skip' ? 'border-gray-200 dark:border-gray-600 text-gray-400' :
                              isRequired ? 'border-blue-400 dark:border-blue-500 text-blue-700 dark:text-blue-300 font-medium' :
                              'border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-100'
                            }`}>
                            {MAPPING_OPTIONS.map(o => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                          {/* ⚠ only while AI low-confidence and user hasn't confirmed this row */}
                          {lowConf && (
                            <span title={`Low confidence suggestion (${suggestion.confidence}%) — select a value to confirm`} className="text-amber-500 shrink-0">⚠</span>
                          )}
                          {/* ✓ green: user manually confirmed OR AI high-confidence match */}
                          {!lowConf && current !== 'skip' && (touched || (suggestion && current === suggestion.target)) && (
                            <span title={touched ? 'Manually confirmed' : `AI suggestion (${suggestion!.confidence}% confidence)`} className="text-green-500 shrink-0 text-[10px]">✓</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Preview section */}
          {preview.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                Mapping Preview — first {preview.length} rows
              </h3>
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="min-w-full text-xs divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-semibold text-gray-500 uppercase tracking-wide">#</th>
                      {previewCols.map(c => (
                        <th key={c.key} className="px-2 py-1.5 text-left font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{c.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {preview.map((item, i) => (
                      <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-2 py-1.5 text-gray-400">{i + 1}</td>
                        {previewCols.map(c => (
                          <td key={c.key} className="px-2 py-1.5 text-gray-700 dark:text-gray-300 max-w-[200px] truncate" title={item[c.key]}>
                            {item[c.key] || <span className="text-gray-300">—</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-400 mt-1.5">Verify each column looks correct. Change any mapping above — preview updates automatically.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 shrink-0 flex items-center justify-between gap-4 flex-wrap">
          <div>
            {missingRequired.length > 0 ? (
              <p className="text-sm text-red-600 dark:text-red-400">
                Must map: <strong>{missingRequired.map(f => optionLabel(f).replace(' *', '')).join(', ')}</strong>
                <span className="text-xs text-gray-400 ml-2">— find those columns above and set their mapping</span>
              </p>
            ) : missingRecommended.length > 0 ? (
              <p className="text-sm text-amber-600 dark:text-amber-400">
                ⚠ Recommended not mapped: <strong>{missingRecommended.map(f => optionLabel(f).replace(' *', '')).join(', ')}</strong>
                <span className="text-xs text-gray-400 ml-2">— items will import without these fields</span>
              </p>
            ) : (
              <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                ✓ Ready to confirm
              </p>
            )}
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
              Cancel
            </button>
            <button
              onClick={() => !confirming && onConfirm(mapping)}
              disabled={missingRequired.length > 0 || confirming}
              title={missingRequired.length > 0
                ? `Missing: ${missingRequired.map(f => optionLabel(f).replace(' *', '')).join(', ')}`
                : undefined}
              className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2">
              {confirming && (
                <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
              )}
              {confirming
                ? 'Processing…'
                : missingRequired.length > 0
                  ? `Confirm Mapping — must map: ${missingRequired.map(f => optionLabel(f).replace(' *', '')).join(', ')}`
                  : `Confirm Mapping (${columns.length} columns) →`}
            </button>
          </div>
        </div>

      </div>
    </div>
  )
}
